import { httpClient } from '../http-client'
import type { PublicCertificateResponse, SaveStudentSignatureInput, StudentCertificate, StudentSignature } from '@/types/certificate'

/**
 * CertificateService — certificados do aluno (módulo `certificates` do backend).
 * O fluxo legado (produtos ainda no modelo antigo) continua pela rota Next
 * `/api/member-areas/certificate`, apontada em `item.legacy.downloadPath`.
 */
class CertificateService {
  /** Um item por mentoria que tem certificado (novo fluxo ou legado). */
  async listMine(): Promise<StudentCertificate[]> {
    const response = await httpClient.get<StudentCertificate[]>('/certificates/me')
    return response.data
  }

  /** Pede a emissão; `document` só quando o certificado exige CPF. */
  async issue(productId: string, document?: string): Promise<StudentCertificate> {
    const response = await httpClient.post<StudentCertificate>(`/certificates/me/${productId}/issue`, document ? { document } : {})
    return response.data
  }

  /** Assinatura salva do aluno (null enquanto ele nunca assinou). */
  async getSignature(): Promise<StudentSignature | null> {
    const response = await httpClient.get<StudentSignature | null>('/certificates/me/signature')
    // Sem assinatura o backend responde 200 com corpo vazio, que o axios entrega como ''.
    const data = response.data
    return data && typeof data === 'object' ? data : null
  }

  /** Salva (ou refaz) a assinatura. Vale para os próximos certificados; os emitidos não mudam. */
  async saveSignature(input: SaveStudentSignatureInput): Promise<StudentSignature> {
    const response = await httpClient.put<StudentSignature>('/certificates/me/signature', input)
    return response.data
  }

  /** URL que conta o download e redireciona para o PDF. */
  downloadUrl(issueId: string): string {
    return `/api/v1/certificates/me/${issueId}/download`
  }

  async getPublic(code: string): Promise<PublicCertificateResponse> {
    const response = await httpClient.get<PublicCertificateResponse>(`/certificates/public/${encodeURIComponent(code)}`)
    return response.data
  }

  /** Download pelo fluxo legado (modelo antigo da mentoria). */
  async downloadLegacy(downloadPath: string, documentNumber?: string): Promise<Blob> {
    const url = documentNumber ? `${downloadPath}&doc=${encodeURIComponent(documentNumber)}` : downloadPath
    const response = await fetch(url, { method: 'GET' })
    if (!response.ok) {
      const errorText = await response.text()
      // 400 = o backend recusou porque o aluno ainda não concluiu: a tela mostra "ainda não disponível".
      throw Object.assign(new Error(errorText || 'Failed to download certificate'), { status: response.status })
    }
    return await response.blob()
  }
}

export const certificateService = new CertificateService()
